import { useEffect, useState } from "react";
import RbacService, { Role } from "@/app/_services/RbacService";
import { toast } from "@/app/_utils/toast";

export function useRoleFormFlow({
  editingRole,
  onSaved,
}: {
  editingRole?: Role | null;
  onSaved: () => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setName(editingRole?.name ?? "");
    setDescription(editingRole?.description ?? "");
    setSelected(editingRole?.permissions ?? []);
    setError(null);
  }, [editingRole]);

  function togglePermission(id: number) {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
    );
  }

  function toggleGroup(ids: number[], checked: boolean) {
    setSelected((prev) =>
      checked
        ? Array.from(new Set([...prev, ...ids]))
        : prev.filter((value) => !ids.includes(value))
    );
  }

  async function handleSave() {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    setError(null);
    setIsSaving(true);
    try {
      if (editingRole) {
        await RbacService.updateRoleAsync(editingRole.id, {
          // A system role's name is referenced in code (the Super Admin check), so only
          // its description and grants are editable.
          ...(editingRole.is_system ? {} : { name: name.trim() }),
          description: description.trim(),
          permissions: selected,
        });
        toast.success("Role updated");
      } else {
        await RbacService.createRoleAsync({
          name: name.trim(),
          description: description.trim(),
          permissions: selected,
        });
        toast.success("Role created");
      }
      onSaved();
    } catch (e: any) {
      toast.error(e?.message || "Could not save the role");
    } finally {
      setIsSaving(false);
    }
  }

  return {
    name,
    setName,
    description,
    setDescription,
    selected,
    togglePermission,
    toggleGroup,
    error,
    isSaving,
    handleSave,
  };
}
